
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { MessageCircle } from 'lucide-react';
import SendMessageModal from '@/components/SendMessageModal';
import { EmailService } from '@/services/emailService';

interface LessonStatusManagerProps {
  item: any;
  onStatusUpdate?: () => void;
}

const LessonStatusManager = ({ item, onStatusUpdate }: LessonStatusManagerProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const canChangeStatus = () => {
    // Only tutors can change status of bookings
    return user?.role === 'tutor';
  };

  const canJoinLesson = () => {
    // Students can only join if lesson is confirmed
    // Tutors can join confirmed or pending lessons
    if (user?.role === 'tutor') {
      return item.status === 'confirmed' || item.status === 'pending';
    }
    return item.status === 'confirmed';
  };

  const updateStatus = async (newStatus: string) => {
    setIsUpdating(true);
    try {
      await pb.collection('bookings').update(item.id, {
        status: newStatus,
      });
      
      // Send approval email when booking is approved
      if (newStatus === 'confirmed') {
        console.log('📧 Sending booking approval emails...');
        
        try {
          // Get student data
          let studentData = { name: 'Student', email: 'samehmakar87@gmail.com', phone: 'Not provided' };
          if (item.expand?.studentId) {
            // If studentId is expanded, it contains user data directly
            studentData = {
              name: item.expand.studentId.name || 'Student',
              email: item.expand.studentId.email || 'samehmakar87@gmail.com',
              phone: item.expand.studentId.phoneNumber || 'Not provided'
            };
          }
          
          // Get tutor data - need to fetch the user info from tutors table
          let tutorData = { name: 'Tutor', email: '', phoneNumber: 'Not provided' };
          if (item.expand?.tutorId) {
            // Fetch the tutor's user data to get the email and phone
            try {
              const tutorRecord = await pb.collection('tutors').getOne(item.expand.tutorId.id, {
                expand: 'user'
              });
              
              console.log('🔍 Fetched tutor record for phone:', tutorRecord);
              
              if (tutorRecord.expand?.user) {
                tutorData = {
                  name: tutorRecord.expand.user.name || tutorRecord.name || 'Tutor',
                  email: tutorRecord.expand.user.email || '',
                  phoneNumber: tutorRecord.phoneNumber || 'Not provided'
                };
              } else if (tutorRecord.user) {
                // If user field exists but not expanded, fetch it separately
                const userRecord = await pb.collection('users').getOne(tutorRecord.user);
                tutorData = {
                  name: userRecord.name || tutorRecord.name || 'Tutor',
                  email: userRecord.email || '',
                  phoneNumber: tutorRecord.phoneNumber || 'Not provided'
                };
              }
              
              console.log('📞 Final tutor data with phoneNumber:', tutorData);
            } catch (tutorError) {
              console.warn('Could not fetch tutor user data:', tutorError);
              // Use expanded data if available, ensuring we check for phoneNumber field
              tutorData = {
                name: item.expand.tutorId.name || 'Tutor',
                email: item.expand.tutorId.email || '',
                phoneNumber: item.expand.tutorId.phoneNumber || 'Not provided'
              };
              console.log('📞 Fallback tutor data with phoneNumber:', tutorData);
            }
          }
          
          console.log('📧 Final student data:', studentData);
          console.log('📧 Final tutor data:', tutorData);
          
          const bookingData = EmailService.formatBookingForEmail(item, studentData, tutorData);
          
          console.log('📧 Formatted booking data for approval email:', bookingData);
          
          const emailResult = await EmailService.sendBookingApprovalNotification(bookingData);
          
          if (emailResult.success) {
            console.log('✅ Booking approval emails sent successfully');
          } else {
            console.warn('⚠️ Some booking approval emails failed:', emailResult.message);
          }
        } catch (emailError) {
          console.error('❌ Failed to send booking approval emails:', emailError);
        }
      }
      
      toast({
        title: "Status Updated!",
        description: `Booking status updated to ${newStatus}`,
      });
      onStatusUpdate?.();
    } catch (error: any) {
      console.error('Error updating booking status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update booking status",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getTutorData = () => {
    // For students viewing their bookings, get tutor info
    if (user?.role === 'student' && item.expand?.tutorId) {
      return {
        id: item.expand.tutorId.id,
        name: item.expand.tutorId.name,
        email: item.expand.tutorId.email,
        user: item.expand.tutorId.user || item.expand.tutorId.id,
        subjects: [item.subject || 'General'],
        hourlyRate: item.expand.tutorId.hourlyRate || 0,
        bio: item.expand.tutorId.bio || '',
        shortBio: item.expand.tutorId.shortBio || '',
        languages: item.expand.tutorId.languages || [],
        experience: item.expand.tutorId.experience || '',
        education: item.expand.tutorId.education || [],
        availability: item.expand.tutorId.availability || { days: [], timeSlots: [] },
        introVideo: item.expand.tutorId.introVideo || '',
        profilePicture: item.expand.tutorId.profilePicture || '',
        profilePictureUrl: item.expand.tutorId.profilePictureUrl || '',
        rating: item.expand.tutorId.rating || 0,
        reviewCount: item.expand.tutorId.reviewCount || 0,
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        collectionId: 'tutors',
        collectionName: 'tutors',
        expand: {
          user: {
            id: item.expand.tutorId.user || item.expand.tutorId.id,
            name: item.expand.tutorId.name,
            email: item.expand.tutorId.email,
            role: 'tutor' as const,
            collectionId: 'users',
            collectionName: 'users'
          }
        }
      };
    }
    return null;
  };

  const tutorData = getTutorData();

  const renderStatusBadge = () => {
    const status = item.status;
    return (
      <Badge className={getStatusColor(status)}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const renderStatusControls = () => {
    const status = item.status;

    // For students, show badge and message button only
    if (user?.role === 'student') {
      return (
        <div className="flex items-center gap-2">
          {renderStatusBadge()}
          {tutorData && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsMessageModalOpen(true)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <MessageCircle className="h-3 w-3 mr-1" />
              Message
            </Button>
          )}
        </div>
      );
    }

    // For tutors, show full controls
    if (!canChangeStatus()) {
      return renderStatusBadge();
    }

    return (
      <div className="flex items-center gap-2">
        {renderStatusBadge()}
        
        <div className="flex gap-1">
          {status === 'pending' && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateStatus('confirmed')}
                disabled={isUpdating}
                className="text-green-600 hover:text-green-700 hover:bg-green-50"
              >
                {isUpdating ? 'Updating...' : 'Approve'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateStatus('cancelled')}
                disabled={isUpdating}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                {isUpdating ? 'Updating...' : 'Decline'}
              </Button>
            </>
          )}
          
          {status === 'confirmed' && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateStatus('completed')}
                disabled={isUpdating}
                className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
              >
                {isUpdating ? 'Updating...' : 'Complete'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateStatus('cancelled')}
                disabled={isUpdating}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                {isUpdating ? 'Updating...' : 'Cancel'}
              </Button>
            </>
          )}
          
          {(status === 'completed' || status === 'cancelled') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateStatus('pending')}
              disabled={isUpdating}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              {isUpdating ? 'Updating...' : 'Reopen'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {renderStatusControls()}
      
      {/* Message Modal for Students */}
      {tutorData && (
        <SendMessageModal
          isOpen={isMessageModalOpen}
          onClose={() => setIsMessageModalOpen(false)}
          tutor={tutorData}
        />
      )}
    </>
  );
};

export default LessonStatusManager;
